import React, { useState, useEffect, useCallback, useReducer, useContext, useMemo } from 'react'
import { Flex } from 'reflexbox/styled-components'
import { toast } from 'react-toastify'
import { ThemeContext } from 'styled-components'

import Button from 'components/Button'
import Loader from 'components/Loader'
import Modal from 'components/Modal/Modal'
import Card from 'components/Card'
import TextButton from 'components/TextButton'
import DataListingTable from 'components/DataListingTable'
import { Text, SemiTitle } from 'components/Typography'
import { Close } from 'components/Icons'
import { CloseButton, AcceptIcon, RejectIcon } from './styles'

import { reducer as loadingReducer } from 'helpers/loadingReducer'
import { getPreRegistrations, approvePreRegistration, rejectPreRegistration } from 'services/partners'

const PreRegistrationList = () => {
  const [preRegistrations, setPreRegistrations] = useState([])
  const [requestStatus, dispatch] = useReducer(loadingReducer, { isLoading: true, error: false })
  const [preRegistrationToApprove, setPreRegistrationToApprove] = useState(null)
  const [preRegistrationToReject, setPreRegistrationToReject] = useState(null)
  const [isLoading, setLoading] = useState(false)
  const [sortBy, setSortBy] = useState(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  })
  const [isRefusedList, setIsRefusedList] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Razão Social',
        accessor: 'company_name',
      },
      {
        Header: 'CNPJ',
        accessor: 'document',
      },
      {
        Header: 'Responsável',
        accessor: 'responsible_name',
      },
      {
        Header: 'E-mail',
        accessor: 'email',
      },
      {
        Header: isRefusedList ? '' : 'Ações',
        accessor: 'actions',
      },
    ],
    [isRefusedList],
  )

  const theme = useContext(ThemeContext)

  const handleTableDataFetch = ({ sortBy, currentPage, pageSize }) => {
    setSortBy(sortBy)
    setPagination({ ...pagination, currentPage, pageSize })
  }

  const fetchPreRegistrations = useCallback(async () => {
    setIsFetching(true)
    try {
      const res = await getPreRegistrations({ pagination, sortBy, refused: isRefusedList || undefined })

      const formattedData = res.preRegistrations.map(
        ({ id, company_name, responsible_name, document, user: { email } }) => ({
          id,
          company_name,
          responsible_name,
          document,
          email,
          actions: !isRefusedList && (
            <Flex alignItems='center'>
              <TextButton variant='primary' onClick={() => setPreRegistrationToApprove({ id, company_name })} mr='8px'>
                <AcceptIcon />
                Aceitar
              </TextButton>
              <TextButton variant='tertiary' onClick={() => setPreRegistrationToReject({ id, company_name })}>
                <RejectIcon />
                Recusar
              </TextButton>
            </Flex>
          ),
        }),
      )

      setPreRegistrations(formattedData)
      setPagination(res.pagination)
      dispatch({ type: 'success' })
    } catch (err) {
      console.log(err)

      if (err?.response?.status === 404) {
        setPagination({ ...pagination, currentPage: pagination.currentPage - 1 })
      } else {
        toast.error('Não foi possível carregar os pré-cadastros no momento')
        dispatch({ type: 'error' })
      }
    } finally {
      setIsFetching(false)
    }
  }, [pagination, isRefusedList])

  const handlePreRegistrationApproval = async () => {
    try {
      setLoading(true)

      await approvePreRegistration(preRegistrationToApprove?.id)

      setPreRegistrationToApprove(null)

      toast.success(`Cadastro do parceiro ${preRegistrationToApprove?.company_name} aprovado com sucesso!`)
    } catch (err) {
      console.log(err)
      toast.error(`Não foi possível aprovar o cadastro do parceiro ${preRegistrationToApprove?.company_name}.`)
    } finally {
      setLoading(false)
      fetchPreRegistrations()
    }
  }

  const handlePreRegistrationRejection = async () => {
    try {
      setLoading(true)

      await rejectPreRegistration(preRegistrationToReject?.id)

      setPreRegistrationToReject(null)

      toast.success(`Cadastro do parceiro ${preRegistrationToReject?.company_name} recusado com sucesso!`)
    } catch (err) {
      console.log(err)
      toast.error(`Não foi possível recusar o cadastro do parceiro ${preRegistrationToReject?.company_name}.`)
    } finally {
      setLoading(false)
      fetchPreRegistrations()
    }
  }

  useEffect(() => {
    fetchPreRegistrations()
  }, [pagination.currentPage, pagination.pageSize, isRefusedList])

  return (
    <Flex width={1} flexDirection='column'>
      <Flex width={1} justifyContent='space-between' alignItems='center'>
        <h1>Pré Cadastros</h1>

        <Button size='auto' onClick={() => setIsRefusedList(!isRefusedList)} disabled={isFetching}>
          {isFetching ? <Loader /> : isRefusedList ? 'Listar aguardando' : 'Listar recusados'}
        </Button>
      </Flex>

      <DataListingTable
        columns={tableColumns}
        data={preRegistrations}
        loading={requestStatus.isLoading}
        pagination={pagination}
        fetchData={handleTableDataFetch}
      />

      <Modal isOpened={!!preRegistrationToApprove} setIsOpened={() => setPreRegistrationToApprove(null)}>
        <Card bg={theme.colors.white} minWidth={588}>
          <Flex flexDirection='column'>
            <Flex width={1} justifyContent='space-between' mb={16}>
              <SemiTitle>Aprovar parceiro {preRegistrationToApprove?.company_name}</SemiTitle>
              <CloseButton onClick={() => setPreRegistrationToApprove(null)}>
                <Close />
              </CloseButton>
            </Flex>

            <Text>Tem certeza que deseja aprovar este parceiro?</Text>

            <Flex mt={24} width={1} justifyContent='flex-end'>
              <Button mr={24} outlined variant='primary' onClick={() => setPreRegistrationToApprove(null)}>
                Cancelar
              </Button>
              <Button variant='primary' disabled={isLoading} onClick={handlePreRegistrationApproval}>
                {isLoading ? <Loader /> : 'Aceitar'}
              </Button>
            </Flex>
          </Flex>
        </Card>
      </Modal>

      <Modal isOpened={!!preRegistrationToReject} setIsOpened={() => setPreRegistrationToReject(null)}>
        <Card bg={theme.colors.white} minWidth={588}>
          <Flex flexDirection='column'>
            <Flex width={1} justifyContent='space-between' mb={16}>
              <SemiTitle>Recusar parceiro {preRegistrationToReject?.company_name}</SemiTitle>
              <CloseButton onClick={() => setPreRegistrationToReject(null)}>
                <Close />
              </CloseButton>
            </Flex>

            <Text>Tem certeza que deseja recusar este parceiro?</Text>

            <Flex mt={24} width={1} justifyContent='flex-end'>
              <Button mr={24} outlined variant='tertiary' onClick={() => setPreRegistrationToReject(null)}>
                Cancelar
              </Button>
              <Button variant='tertiary' disabled={isLoading} onClick={handlePreRegistrationRejection}>
                {isLoading ? <Loader /> : 'Recusar'}
              </Button>
            </Flex>
          </Flex>
        </Card>
      </Modal>
    </Flex>
  )
}

export default PreRegistrationList
